import { useCallback, type FC, type ReactNode } from "react";
import {
  Controller,
  useController,
  useWatch,
  type Control,
  type FieldError,
  type Path,
  type UseFormSetValue,
} from "react-hook-form";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { BooleanTriStateCheckbox, Checkbox } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";
import { useOnMouseEventPreventDefaultHandler } from "@ds/utils/handlers";
import { isFormationPropertyRequired } from "@ds/utils/validation";

export const BooleanFormationControl: FC<{
  name: `formation.properties.${string}` | `template.experience_formation.properties.${string}`;
  control: Control<Experience | Project>;
  setValue: UseFormSetValue<Experience | Project>;
  property: TemplateBooleanFormationProperty;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBlur?: () => void;
}> = ({ name, control, setValue, property, autoFocus, excludeLabel, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const onMouseDownHandler = useOnMouseEventPreventDefaultHandler();

  const entity = useWatch({ control }) as Experience | Project;
  const mainFieldPath = `${name}.value` as Path<Experience | Project>;
  const proxyFieldPath = `metadata.proxyFields.${name}.value` as Path<Experience | Project>;

  const {
    fieldState: { error: mainFieldError },
  } = useController({ name: mainFieldPath, control });

  const hasMultipleValues = isMultipleFieldValuesInAggregatedEntity(entity, mainFieldPath as Path<Experience>);

  const onChangeHandler = useCallback(
    (onChange: (...event: unknown[]) => void) => (newValue?: boolean) => {
      setValue(mainFieldPath, newValue ?? false, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      onChange(newValue);
    },
    [mainFieldPath, setValue],
  );

  const getLabelElement = useCallback(
    (labelError: FieldError | undefined, inputElement: ReactNode) => (
      <LabelWrapper
        label={property.title}
        className="col-8"
        isRequired={isFormationPropertyRequired(property.metadata?.validation_rules)}
        error={labelError}
      >
        {inputElement}
      </LabelWrapper>
    ),
    [property.title, property.metadata?.validation_rules],
  );

  if (hasMultipleValues) {
    return (
      <Controller
        name={proxyFieldPath}
        control={control}
        render={({ field: { ref, value, onChange, onBlur: onBlurFn, ...restFields }, fieldState }) => {
          const element = (
            <BooleanTriStateCheckbox
              ref={ref}
              isChecked={value as boolean}
              autoFocus={autoFocus}
              onChange={onChangeHandler(onChange)}
              onBlur={onBlurHandler(onBlurFn)}
              onMouseDown={onMouseDownHandler}
              {...restFields}
            />
          );

          return excludeLabel ? element : getLabelElement(mainFieldError || fieldState.error, element);
        }}
      />
    );
  }

  return (
    <Controller
      name={mainFieldPath}
      control={control}
      render={({ field: { ref, value, onBlur: onBlurFn, ...restFields }, fieldState }) => {
        const element = (
          <Checkbox
            ref={ref}
            checked={!!value}
            autoFocus={autoFocus}
            onBlur={onBlurHandler(onBlurFn)}
            onMouseDown={onMouseDownHandler}
            {...restFields}
          />
        );

        return excludeLabel ? element : getLabelElement(mainFieldError || fieldState.error, element);
      }}
    />
  );
};
